import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import LoadingButton from "../../../components/LoadingButton";

interface CreateFolderDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateFolder: (folderName: string) => Promise<void>;
  loading?: boolean;
}

export default function CreateFolderDialog({
  open,
  onClose,
  onCreateFolder,
  loading = false,
}: CreateFolderDialogProps) {
  const [folderName, setFolderName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Combined loading state to prevent race conditions
  const isLoading = loading || isSubmitting;

  const handleClose = () => {
    if (!isLoading) {
      setFolderName("");
      onClose();
    }
  };

  const handleCreate = async () => {
    if (folderName.trim() && !isLoading) {
      setIsSubmitting(true);
      try {
        await onCreateFolder(folderName.trim());
        setFolderName("");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Create Folder
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Create a new folder to organize your documents. Folder tags will automatically show unique tags from documents within the folder.
        </Typography>
        <TextField
          fullWidth
          label="Folder Name"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          autoFocus
          disabled={isLoading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleCreate}
          loading={isLoading}
          disabled={!folderName.trim() || isLoading}
        >
          Create
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}